import { useEffect, useState } from "react";
import { TopBar } from "../../components/TopBar";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { StatusBadge } from "../../components/Badge";
import { useProjects } from "../../context/ProjectContext";
import { useToast } from "../../context/ToastContext";
import { useAsync } from "../../lib/useAsync";
import { useLiveRanks, type LiveRank } from "../../lib/useLiveRanks";
import { api } from "../../api/client";
import { timeAgo } from "../../lib/format";
import type { PlayerBoard, PlayerSummary } from "../../api/types";

const RECENT_MS = 5 * 60 * 1000;
const initial = (s: string) => (s || "?").charAt(0).toUpperCase();

export function Players() {
  const { active } = useProjects();
  const pid = active?.id ?? "";
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const { data, loading, error } = useAsync(() => api.listPlayers(pid, query || undefined), [pid, query]);
  const players = data ?? [];

  // Auto-select the first player once a list loads and nothing is selected (or selection fell out).
  useEffect(() => {
    if (players.length === 0) return;
    if (!selected || !players.some((p) => p.player_id === selected)) {
      setSelected(players[0].player_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players]);

  return (
    <>
      <TopBar crumbs={<b>Players</b>} />
      <div className="content">
        <PageHeader
          title="Players"
          intent="Who's playing, what they've done, and how they're ranked right now."
        />

        <div className="players">
          <div className="player-list">
            <input
              className="input search"
              placeholder="Search players…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {loading && <p className="muted" style={{ padding: "8px 4px" }}>Loading…</p>}
            {error && <p className="err" style={{ padding: "8px 4px" }}>{error}</p>}
            {!loading && !error && players.length === 0 && (
              <p className="muted" style={{ padding: "8px 4px" }}>
                {query ? "No players match." : "No players have submitted yet."}
              </p>
            )}
            <div className="player-rows">
              {players.map((p) => (
                <PlayerRow
                  key={p.player_id}
                  p={p}
                  active={p.player_id === selected}
                  onClick={() => setSelected(p.player_id)}
                />
              ))}
            </div>
          </div>

          <div className="player-detail">
            {selected ? (
              <PlayerDetail projectId={pid} player={players.find((p) => p.player_id === selected) ?? null} playerId={selected} />
            ) : (
              !loading && (
                <EmptyState icon="🎮" title="Select a player">
                  Pick a player on the left to see the boards they're on and their submissions.
                </EmptyState>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function PlayerRow({ p, active, onClick }: { p: PlayerSummary; active: boolean; onClick: () => void }) {
  const recent = Date.now() - new Date(p.last_activity).getTime() < RECENT_MS;
  return (
    <button className="player-row" data-active={active} onClick={onClick}>
      <span className="pa">{initial(p.player_id)}</span>
      <span className="grow" style={{ minWidth: 0 }}>
        <span className="pn">{p.player_id}</span>
        <span className="pm">
          {p.board_count} board{p.board_count === 1 ? "" : "s"} · {timeAgo(p.last_activity)}
        </span>
      </span>
      {recent && <span className="dot" title="Active in the last 5 minutes" />}
    </button>
  );
}

function PlayerDetail({
  projectId,
  player,
  playerId,
}: {
  projectId: string;
  player: PlayerSummary | null;
  playerId: string;
}) {
  const toast = useToast();
  const { data, loading, error } = useAsync(() => api.playerBoards(projectId, playerId), [projectId, playerId]);
  const boards = data ?? [];
  const liveRanks = useLiveRanks(projectId, playerId, boards.map((b) => b.leaderboard_id));

  function copyId() {
    navigator.clipboard?.writeText(playerId).then(
      () => toast("Player id copied"),
      () => toast("Could not copy", "err"),
    );
  }

  return (
    <div>
      <div className="player-head">
        <span className="pa">{initial(playerId)}</span>
        <div className="grow" style={{ minWidth: 0 }}>
          <h2>{playerId}</h2>
          <div className="sub">
            {player
              ? `${player.submission_count} submission${player.submission_count === 1 ? "" : "s"} across ${player.board_count} board${player.board_count === 1 ? "" : "s"} · last seen ${timeAgo(player.last_activity)}`
              : " "}
          </div>
        </div>
        <button className="api-key-chip" onClick={copyId} title="Copy this player's id for the API/SDK">
          copy id
        </button>
      </div>

      {loading && <p className="muted">Loading boards…</p>}
      {error && <p className="err">{error}</p>}
      {!loading && !error && boards.length === 0 && (
        <EmptyState icon="🗒️" title="No submissions">
          This player hasn't submitted to any board in this project.
        </EmptyState>
      )}

      {boards.map((b) => (
        <BoardCard
          key={b.leaderboard_id}
          projectId={projectId}
          playerId={playerId}
          board={b}
          live={liveRanks[b.leaderboard_id]}
        />
      ))}
    </div>
  );
}

function BoardCard({
  projectId,
  playerId,
  board,
  live,
}: {
  projectId: string;
  playerId: string;
  board: PlayerBoard;
  live?: LiveRank;
}) {
  const [open, setOpen] = useState(false);
  const held = board.pending_count;
  const score = live ? live.score : board.latest_score;

  return (
    <div className="card pbc">
      <div className="pbc-top" onClick={() => setOpen((o) => !o)}>
        <div style={{ minWidth: 0 }}>
          <div className="pbc-name">{board.leaderboard_id}</div>
          <div className="pbc-meta">
            <span>
              {board.submission_count} submission{board.submission_count === 1 ? "" : "s"}
            </span>
            {held > 0 && <span>· {held} held</span>}
            {board.rejected_count > 0 && <span>· {board.rejected_count} rejected</span>}
            <span>· {timeAgo(board.last_activity)}</span>
          </div>
        </div>
        <div className="pbc-rank">
          <RankValue rank={live?.rank} />
          <div className="sc">{score.toLocaleString()}</div>
          {live && (
            <div className="live">
              <span className="dot" /> live
            </div>
          )}
        </div>
      </div>

      {open && <SubmissionTimeline projectId={projectId} playerId={playerId} board={board.leaderboard_id} />}
    </div>
  );
}

/** The big rank number; flashes (rank-pop) whenever the live rank changes. */
function RankValue({ rank }: { rank?: number }) {
  const [pop, setPop] = useState(false);
  const [prev, setPrev] = useState<number | undefined>(rank);
  useEffect(() => {
    if (rank !== undefined && rank !== prev) {
      setPop(true);
      const t = setTimeout(() => setPop(false), 500);
      setPrev(rank);
      return () => clearTimeout(t);
    }
    setPrev(rank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rank]);

  if (rank === undefined) return <div className="rk muted" style={{ fontSize: 18 }}>unranked</div>;
  return (
    <div className={`rk${pop ? " rank-pop" : ""}`}>
      <span className="hash">#</span>
      {rank}
    </div>
  );
}

function SubmissionTimeline({ projectId, playerId, board }: { projectId: string; playerId: string; board: string }) {
  const { data, loading, error } = useAsync(
    () => api.playerSubmissions(projectId, playerId, board),
    [projectId, playerId, board],
  );
  const subs = data ?? [];

  return (
    <div className="sub-timeline">
      {loading && <p className="muted" style={{ fontSize: 13 }}>Loading submissions…</p>}
      {error && <p className="err" style={{ fontSize: 13 }}>{error}</p>}
      {!loading && !error && subs.length === 0 && (
        <p className="muted" style={{ fontSize: 13 }}>No submissions recorded.</p>
      )}
      {subs.map((s) => {
        const blob = (s.metadata as Record<string, unknown> | null | undefined)?._blob as
          | { content_type?: string }
          | undefined;
        return (
          <div className="sub-item" key={s.id}>
            <span className="ss mono">{s.score.toLocaleString()}</span>
            <span className="when">{timeAgo(s.created_at)}</span>
            <span className="extra">
              {blob && (
                <span className="replay-link" title="A replay/ghost is attached to this submission">
                  ▶ replay
                </span>
              )}
              <StatusBadge status={s.status} />
            </span>
          </div>
        );
      })}
    </div>
  );
}
